/**
 * Country metadata used across the application.
 *
 * @example
 * const uae: Country = {
 *   id: 1,
 *   name: 'United Arab Emirates',
 *   slug: 'united-arab-emirates',
 *   code: 'AE',
 *   dial_code: '+971',
 * };
 */
export type Country = {
  /** Unique numeric identifier. */
  id: number;
  /** Human-readable name (English). */
  name: string;
  /** Slugified country name. */
  slug: string;
  /** ISO 3166-1 alpha-2 code. */
  code: string;
  /** International dialing code with leading '+'. */
  dial_code: string;
  /** Optional emoji or icon URL for the flag. */
  flag?: string;
  /** Optional default currency code. */
  currency?: string;
};

/**
 * Canonical IDs for supported countries.
 *
 * @example
 * const id: CountryId = CountryIds.UAE; // 1
 */
export const CountryIds = {
  UAE: 1,
  KSA: 2,
  Oman: 3,
  Qatar: 4,
  Kuwait: 5,
  Bahrain: 6,
} as const;

/**
 * Union of supported country numeric IDs.
 */
export type CountryId = (typeof CountryIds)[keyof typeof CountryIds];

/**
 * Canonical ISO codes for supported countries.
 *
 * @example
 * const code: CountryCode = CountryCodes.UAE; // 'AE'
 */
export const CountryCodes = {
  UAE: 'AE',
  KSA: 'SA',
  Oman: 'OM',
  Qatar: 'QA',
  Kuwait: 'KW',
  Bahrain: 'BH',
} as const;

/**
 * Union of supported country ISO alpha-2 codes.
 */
export type CountryCode = (typeof CountryCodes)[keyof typeof CountryCodes];
